import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  TablePagination,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  UploadFile as UploadFileIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { studentsApi, Student, StudentStatus } from './api';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useMasterData } from '../../../hooks/useMasterData';

const StudentList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { masters, fetchMasters, fetchSections: fetchSectionsHook } = useMasterData();

  // Data State
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Filter State
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    academicSessionId: '',
    classId: '',
    sectionId: '',
    status: ''
  });

  // Master Data State
  const [sections, setSections] = useState<any[]>([]);

  // Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);

  useEffect(() => {
    fetchMasters();
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [page, limit, filters, search]);

  useEffect(() => {
    if (filters.classId) {
      (async () => {
        const secs = await fetchSectionsHook(filters.classId);
        setSections(secs);
      })();
    } else {
      setSections([]);
    }
  }, [filters.classId]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await studentsApi.getAll({
        page: page + 1,
        limit,
        search,
        academicSessionId: filters.academicSessionId || undefined,
        classId: filters.classId || undefined,
        sectionId: filters.sectionId || undefined,
        status: filters.status || undefined
      });
      setStudents(response.data);
      setTotal(response.meta.total);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (student: Student) => {
    setStudentToDelete(student);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!studentToDelete) return;
    try {
      await studentsApi.delete(studentToDelete.id);
      setDeleteDialogOpen(false);
      fetchStudents();
    } catch (error) {
      alert('Failed to delete student');
    }
  };

  const handleStatusChange = async (student: Student) => {
    const newStatus = student.status === StudentStatus.ACTIVE ? StudentStatus.INACTIVE : StudentStatus.ACTIVE;
    if (window.confirm(`Are you sure you want to change status to ${newStatus}?`)) {
      try {
        await studentsApi.changeStatus(student.id, newStatus);
        fetchStudents();
      } catch (error) {
        
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case StudentStatus.ACTIVE: return 'success';
      case StudentStatus.INACTIVE: return 'error';
      case StudentStatus.PASSED_OUT: return 'info';
      case StudentStatus.TC: return 'warning';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Students
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage student admissions, academic records, and profiles
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5">Student List</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => navigate('/scholar-register/students/bulk-upload')}
              disabled={!hasPermission('STUDENT_CREATE')}
              title={!hasPermission('STUDENT_CREATE') ? 'You do not have permission to bulk upload students' : 'Bulk upload students from file'}
            >
              Bulk Upload
            </Button>
            {hasPermission('STUDENT_CREATE') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/scholar-register/students/create')}
              >
                Add Student
              </Button>
            )}
          </Box>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by Name, Adm No..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Session"
                value={filters.academicSessionId}
                onChange={(e) => handleFilterChange('academicSessionId', e.target.value)}
              >
                <MenuItem value="">All Sessions</MenuItem>
                {(masters.academicSessions || []).map((session) => (
                  <MenuItem key={session.id} value={session.id}>
                    {session.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Class"
                value={filters.classId}
                onChange={(e) => handleFilterChange('classId', e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                {(masters.classes || []).map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Section"
                value={filters.sectionId}
                onChange={(e) => handleFilterChange('sectionId', e.target.value)}
                disabled={!filters.classId}
              >
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((sec) => (
                  <MenuItem key={sec.id} value={sec.id}>
                    {sec.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                {Object.values(StudentStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replace('_', ' ')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={1} sx={{ textAlign: 'right' }}>
              <Button variant="outlined" startIcon={<FilterListIcon />} onClick={fetchStudents}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Adm No</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Father Name</TableCell>
                <TableCell>Class/Section</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    No students found
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell>{student.admissionNumber}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {student.firstName} {student.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        SR: {student.scholarNumber || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{student.parent?.fatherName || '-'}</TableCell>
                    <TableCell>
                      {student.class?.name} - {student.section?.name}
                    </TableCell>
                    <TableCell>{student.phone || student.parent?.fatherMobile || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={student.status}
                        size="small"
                        color={getStatusColor(student.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="View Profile">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/scholar-register/students/${student.id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                        {hasPermission('STUDENT_EDIT') && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/scholar-register/students/edit/${student.id}`)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {hasPermission('STUDENT_ACTIVATE') && (
                          <Tooltip title={student.status === 'ACTIVE' ? "Deactivate" : "Activate"}>
                            <IconButton
                              size="small"
                              color={student.status === 'ACTIVE' ? "warning" : "success"}
                              onClick={() => handleStatusChange(student)}
                            >
                              {student.status === 'ACTIVE' ? <BlockIcon /> : <CheckCircleIcon />}
                            </IconButton>
                          </Tooltip>
                        )}

                        {hasPermission('STUDENT_DELETE') && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(student)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={limit}
            onRowsPerPageChange={handleLimitChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to permanently delete student
              "{studentToDelete?.firstName} {studentToDelete?.lastName}"?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default StudentList;
